/**
 * v0.4 追加要件⑪ 章⑦フォローアップ (D-348、6並列レビューCRITICAL対応): `/settings/report-views` で
 * 定義した列 (`view_columns`) を日別サマリ・従業員横断ビュー・36協定遵守状況の3画面が共有して
 * 描画するためのヘルパー。3画面が個別にフォーマット処理を書くと §8-93 の二重実装になるため、
 * 値の整形ロジックはここへ集約する。
 */
import { formatCurrency, formatCount, formatPercent } from '@/lib/format';
import type { useLabel } from '@/lib/labels';
import type { LabelKey } from '@/lib/label-keys';

export type ViewColumnFormatHint = 'integer' | 'decimal1' | 'percent' | 'currency' | null;

export type ViewColumn = {
  column_id: string;
  label: string;
  // v0.4 章⑦フォローアップ (D-348、design-and-code HIGH指摘): metric 列で label_override が未設定の
  // 場合、report_metric_catalog の label_key を返す (ReportViewsSettingsPage.tsx の metricDisplayLabel と
  // 同じ思想で辞書を解決する)。formula 列や label_override 設定済みの場合は null (label をそのまま使う)。
  label_key: string | null;
  column_kind: 'metric' | 'formula';
  unit: string | null;
  format_hint: ViewColumnFormatHint;
  omitted: boolean;
  omitted_reason: 'metric_unavailable' | null;
  values: Record<string, string | number | boolean | null>;
};

/** ReportViewsSettingsPage.tsx の metricDisplayLabel と同じ規約: label_key があればラベル辞書
 *  (useLabel()) で解決し、無ければ (formula 列 or label_override 設定済み) col.label をそのまま使う。
 *  label_key はバックエンドの report_metric_catalog seed から来る信頼済みの値のため LabelKey へキャストする
 *  (label-keys.ts の LabelKey union はコンパイル時の網羅性チェック用であり、実行時はどんな文字列でも
 *  未登録なら useLabel() が第2引数のフォールバックへ倒れるだけで安全)。 */
export const resolveViewColumnLabel = (t: ReturnType<typeof useLabel>, col: ViewColumn): string =>
  (col.label_key ? t(col.label_key as LabelKey, col.label) : col.label);

/** 数値以外 (bool/string) の評価結果は素の文字列表示に倒す (章⑦の対象メトリクスは全て number のため
 *  実運用では起きない想定だが、AST 評価器自体は汎用のため防御的に扱う)。 */
export const formatViewColumnValue = (value: string | number | boolean | null, formatHint: ViewColumnFormatHint): string => {
  // design-expert Minor指摘: 同じ3画面の既存「値なし」表現 (NullBadge/fmtMinutes/AgreementCell/素の"—"セル) は
  // 一貫して em ダッシュ (U+2014) を使う。マイナス記号 (U+2212) だと同一表内で記号が2種類混在して見える。
  if (value === null) return '—';
  if (typeof value !== 'number' || !Number.isFinite(value)) return String(value);
  switch (formatHint) {
    case 'currency': return formatCurrency(value);
    case 'percent': return formatPercent(value);
    case 'decimal1': return value.toFixed(1);
    case 'integer': return formatCount(Math.round(value));
    default: return formatCount(value);
  }
};
